/**
 * Forgetting an exploration, ACKNOWLEDGED — the one way every door does it.
 *
 * Four doors forget explorations: the library's row and its Forget all, the
 * sidebar's row, and the composer putting down a seed its key refused; the
 * palette's Forget saved explorations opens the library's Forget all, armed
 * (D335). Each did the same thing in the same order:
 * ask the store to delete, take the transcripts away, and — except the composer,
 * which says nothing — announce it done: "Forgotten." for one, "cleared" for all.
 * The store's answer came back before anything had been deleted at all: whether a
 * row had been listed, or which ids, while the deletion itself was fired and not
 * waited for.
 * When the browser refused it, one toast arrived later naming nothing, and the
 * exploration came back on the next boot without its transcript, its pin or its
 * closing-tab checkpoint; the shared workspace's `remove()` did the same with a
 * server's 409 (A04). Saves were already acknowledged — D84 made a "saved"
 * indicator mean an awaited commit — and deletions were the half left behind.
 *
 * So the order is written down once, here, and every door calls it:
 *
 *  1. **The canvas first**, when the exploration is the one open. Clearing it
 *     checkpoints the outgoing document, and a lock held by a pass or a live run
 *     refuses the whole press (`busy`) with nothing touched. The library's row
 *     and both Forget-all doors skipped this, so the next edit saved the
 *     forgotten document straight back, against its own tombstone, and paused
 *     paid work with a conflict nobody had caused.
 *  2. **Whatever is queued is saved**, before the deletion is asked for — see
 *     `settleDurableWrites`. A deletion that can be refused has to leave behind
 *     the exploration as the reader last saw it.
 *  3. **The deletion, awaited**, through whichever remover this workspace has.
 *  4. **Only on acceptance**, the transcripts — of the reader who pressed, named
 *     at the press, so an acceptance landing after a sign-out removes theirs and
 *     not the next reader's — and this tab's recovery copies of what is gone.
 *     A page-only forget, where the shelf could not be read, keeps the
 *     transcripts for the stored copy that comes back, but releases the recovery
 *     copies too: those are this page's unsaved bodies of what the reader just
 *     forgot, and a retry would save them straight back.
 *  5. **On refusal, nothing local moves**, and an exploration whose canvas was
 *     cleared for the press is opened again, so a refused Forget leaves the
 *     reader where they were — once no other deletion of it is still waiting on
 *     an answer (`owed`).
 *
 * And it says what happened (`ForgetOutcome`), rather than whether something
 * was there; `ui/forgetPress.ts` turns that into the sentence a door prints.
 *
 * THE REMOVER IS A SEAM. The personal shelf is the default. A workspace whose
 * explorations live elsewhere — the hosted shared workspace, whose graphs are on
 * a server — installs its own with `setExplorationRemover`, and every door
 * above gets the same order and the same words over it. Its one obligation is
 * the contract below: resolve `forgotten` only once the store that holds the
 * exploration has ACCEPTED the deletion, and `kept` with a reason otherwise —
 * a stale version (`conflict`), a reader who may not (`forbidden`), a server
 * that could not be reached (`unavailable`). Never `forgotten` on the request
 * merely having been sent, which is the defect this module exists to end.
 */

import { forgetAll, forgetSession } from './persist'
import {
  conversationOwner,
  forgetAllConversations,
  forgetConversations,
  type ConversationOwner,
} from './conversations'
import { beginForgetting, endForgetting, reopenKept, settleDurableWrites } from './autosave'
import { useStore } from './store'

/** Why a deletion did not happen. Each is a sentence in `ui/forgetPress.ts`. */
export type ForgetRefusal =
  /** This browser's storage refused the transaction. */
  | 'storage'
  /** The store that holds it could not be reached — offline, or a server down. */
  | 'unavailable'
  /** It changed since it was shown: a newer version stands, and deleting it
   *  would delete something the reader has not seen. */
  | 'conflict'
  /** This reader may not delete it — somebody else's shared graph. */
  | 'forbidden'
  /** A pass or a live run holds the canvas it is open on. The lock's own
   *  sentence has already been said (`newExploration`). */
  | 'busy'

export type ForgetOutcome =
  /** The store accepted the deletion. `had` is false when there was nothing
   *  there to delete, which is a success with a different sentence. */
  | { kind: 'forgotten'; had: boolean }
  /** Nothing was deleted, and nothing local was removed. */
  | { kind: 'kept'; reason: ForgetRefusal; detail?: string }
  /** The shelf could not be read, so only this page's copy went: a stored copy,
   *  if there is one, comes back when the shelf can be read. Not a deletion, and
   *  the transcripts stay for that copy. */
  | { kind: 'page-only'; had: boolean }

export type ForgetAllOutcome =
  | { kind: 'forgotten'; ids: string[] }
  | { kind: 'kept'; reason: ForgetRefusal; detail?: string }
  | { kind: 'page-only'; ids: string[] }

/** What deletes explorations in this workspace. See the head of this file. */
export interface ExplorationRemover {
  /** Resolve only once the store that holds `id` has accepted or refused. */
  forget(id: string): Promise<ForgetOutcome>
  /** Absent where the workspace offers no such door; `canForgetEverything`
   *  then answers false and the doors that would offer it stand down. */
  forgetAll?(): Promise<ForgetAllOutcome>
  /** Open an exploration this workspace holds, for the reopen after a refusal
   *  (step 5) — and only if `stillWanted()` still says so at the moment it can
   *  open, since an open can wait on saves or a server and the reader may have
   *  moved on. The personal shelf's reads only that shelf — a shared graph is not
   *  on it — so a workspace whose explorations live elsewhere supplies its own.
   *  Absent, a refusal leaves the canvas cleared. */
  reopen?(id: string, stillWanted: () => boolean): void | Promise<void>
}

/**
 * The personal shelf. Its queued saves settle before either deletion, for the
 * reason in step 2 above, and an accepted or page-only one releases this tab's
 * recovery copies of what it forgot (`endForgetting`, step 4).
 */
const personalRemover: ExplorationRemover = {
  forget: (id) => personally(id, () => forgetSession(id)),
  forgetAll: () => personally(null, forgetAll),
  reopen: reopenKept,
}

/**
 * One deletion on the personal shelf, bracketed for the save manager
 * (`beginForgetting`, `endForgetting`): saves of what is being deleted are held
 * until it answers, then released on acceptance or reported and saved again on a
 * refusal.
 *
 * The queue is settled once more after the answer, whichever it was, before the
 * hold is lifted. A save queued against the document while its deletion was in
 * flight has then met the tombstone, and one queued just before the record moved
 * has met the record — and both were judged while the hold could still catch
 * them. Lifted any earlier, the second kind was dropped as superseded by this
 * tab's own deletion even when that deletion was refused.
 */
async function personally<T extends ForgetOutcome | ForgetAllOutcome>(
  id: string | null,
  run: () => Promise<T>,
): Promise<T> {
  beginForgetting(id)
  let gone = false
  try {
    await settleDurableWrites()
    const out = await run()
    gone = out.kind !== 'kept'
    await settleDurableWrites()
    return out
  } finally {
    endForgetting(id, gone)
  }
}

let remover: ExplorationRemover = personalRemover

/**
 * Whether the reader has started something on the canvas a press cleared — words
 * in the composer, which no session holds. A reopen after a refusal would carry
 * them off with the canvas they were typed on: the composer's box empties on a
 * change of exploration. The UI answers, about the cleared canvas by its session
 * id (`setReopenGuard`); core cannot read a box.
 */
let startedOn: (sessionId: string) => boolean = () => false
export function setReopenGuard(fn: ((sessionId: string) => boolean) | null): void {
  startedOn = fn ?? (() => false)
}

type Canvas = ReturnType<typeof useStore.getState>['session']

/**
 * Put back the exploration a refused press cleared — only over the blank canvas
 * that press made, and only if the reader has not started anything there. Asked
 * again at the moment the remover can open, not only now.
 *
 * And not while another deletion of it is waiting on an answer, which a press
 * can start while the open waits on saves or a server. Then the reopen is owed
 * again, and that deletion's answer pays it or drops it (`owed`): landing, it put
 * the exploration back just as that deletion removed it.
 */
async function reopenAfterRefusal(id: string, blank: Canvas): Promise<void> {
  const stillWanted = () => {
    if (useStore.getState().session !== blank || startedOn(blank.id)) return false
    if (!coveredElsewhere(id)) return true
    owed.set(id, blank)
    return false
  }
  if (!stillWanted()) return
  try {
    await remover.reopen?.(id, stillWanted)
  } catch (error) {
    // The refusal still stands, and so does its sentence; a reopen that could not
    // be made is said beside it, never in its place.
    const detail = error instanceof Error ? error.message : String(error)
    useStore
      .getState()
      .toast(`It is still in your library, but could not be reopened: ${detail}`, 'warn')
  }
}

/**
 * The deletions still waiting on their remover's answer — `EVERYTHING` for the
 * bulk door — and the reopens a refusal owes but cannot make yet.
 *
 * A single Forget and a Forget-everything can both be in flight over the same
 * exploration. A refusal of one that reopened it at once put it back on the canvas
 * just as the other might delete it; when the other was accepted, the canvas held
 * a document the shelf no longer had, and the next edit was refused as "another
 * tab saved a newer version" with nowhere left to save it. So a refusal that
 * cleared the canvas reopens only once nothing else covering that exploration is
 * waiting on an answer; until then the reopen is owed, and the last answer pays
 * it: a refusal reopens it, an acceptance drops it.
 *
 * Owed rather than awaited: each deletion would wait on the other's whole press,
 * and two presses each owing the other a reopen would wait forever.
 */
const awaitingAnswer = new Set<string>()
const owed = new Map<string, Canvas>()
const coveredElsewhere = (id: string): boolean =>
  awaitingAnswer.has(id) || awaitingAnswer.has(EVERYTHING)

/** One deletion's remover answered: pay or drop the reopens it covered, and owe
 *  the one it would make itself while another deletion of the same exploration is
 *  still waiting. An acceptance drops only what it removed — a workspace's
 *  Forget everything removes the reader's own graphs and leaves a shared one
 *  standing, whose refused Forget is still owed its reopen. */
async function answered(
  key: string,
  out: ForgetOutcome | ForgetAllOutcome,
  cleared: { id: string; blank: Canvas } | null,
): Promise<void> {
  awaitingAnswer.delete(key)
  if (out.kind === 'kept' && cleared) owed.set(cleared.id, cleared.blank)
  const removed = (id: string) =>
    out.kind !== 'kept' && (key === id || ('ids' in out && out.ids.includes(id)))
  const due: [string, Canvas][] = []
  for (const [id, blank] of [...owed]) {
    if (key !== EVERYTHING && key !== id) continue
    if (removed(id)) owed.delete(id)
    else if (!coveredElsewhere(id)) {
      owed.delete(id)
      due.push([id, blank])
    }
  }
  for (const [id, blank] of due) await reopenAfterRefusal(id, blank)
}

/** Install the workspace's remover, or null to return to the personal shelf.
 *  The hosted shell calls this when it enters a shared workspace (D331). */
export function setExplorationRemover(next: ExplorationRemover | null): void {
  remover = next ?? personalRemover
}

/** Whether this workspace has a Forget-everything door at all. */
export function canForgetEverything(): boolean {
  return remover.forgetAll !== undefined
}

/**
 * The deletions in flight, by exploration — and `EVERYTHING` for the bulk door.
 * A second press of any door on the same exploration while one is in flight gets
 * the first one's answer instead of a second deletion, whichever door it came
 * from; and the doors read this to show the press as pending.
 */
const EVERYTHING = '*'
const inFlight = new Map<string, Promise<ForgetOutcome | ForgetAllOutcome>>()
const watchers = new Set<() => void>()

/** Whether a deletion of this exploration — or of everything — is in flight. */
export function forgetting(id: string): boolean {
  return inFlight.has(id) || inFlight.has(EVERYTHING)
}
/** Whether the Forget-everything door is in flight. */
export function forgettingEverything(): boolean {
  return inFlight.has(EVERYTHING)
}
export function subscribeForgetting(fn: () => void): () => void {
  watchers.add(fn)
  return () => {
    watchers.delete(fn)
  }
}
function announce(): void {
  for (const fn of watchers) fn()
}

function track<T extends ForgetOutcome | ForgetAllOutcome>(
  key: string,
  run: () => Promise<T>,
): Promise<T> {
  const running = inFlight.get(key)
  if (running) return running as Promise<T>
  const p = run().finally(() => {
    inFlight.delete(key)
    announce()
  })
  inFlight.set(key, p)
  announce()
  return p
}

/** A remover that threw instead of answering has not accepted anything. */
const unanswered = (error: unknown): { kind: 'kept'; reason: 'unavailable'; detail: string } => ({
  kind: 'kept',
  reason: 'unavailable',
  detail: error instanceof Error ? error.message : String(error),
})

/**
 * Forget one exploration, from any door. Resolves once the deletion has been
 * accepted or refused; see the head of this file for the order and why.
 */
export function forgetExploration(id: string): Promise<ForgetOutcome> {
  return track(id, async () => {
    // Named NOW: the reader who pressed is the one whose transcripts go.
    const pressedBy: ConversationOwner = conversationOwner()
    const st = useStore.getState()
    const wasOpen = st.session.id === id
    if (wasOpen && !st.newExploration()) return { kind: 'kept', reason: 'busy' }
    // The blank canvas the press made, so a refusal reopens only over that —
    // never over somewhere the reader has gone since.
    const blank = useStore.getState().session
    let out: ForgetOutcome
    awaitingAnswer.add(id)
    try {
      out = await remover.forget(id)
    } catch (error) {
      out = unanswered(error)
    }
    if (out.kind === 'forgotten') forgetConversations(id, pressedBy)
    await answered(id, out, wasOpen ? { id, blank } : null)
    return out
  })
}

/**
 * Forget every exploration, from either door that offers it. The open one goes
 * too, as both doors promise, so the canvas is cleared first under the lock.
 */
export function forgetEverything(): Promise<ForgetAllOutcome> {
  return track(EVERYTHING, async () => {
    const forgetAllHere = remover.forgetAll
    if (!forgetAllHere) return { kind: 'kept', reason: 'unavailable' }
    const pressedBy: ConversationOwner = conversationOwner()
    const st = useStore.getState()
    const open = st.session.order.length > 0 ? st.session.id : null
    if (open !== null && !st.newExploration()) return { kind: 'kept', reason: 'busy' }
    const blank = useStore.getState().session
    let out: ForgetAllOutcome
    awaitingAnswer.add(EVERYTHING)
    try {
      out = await forgetAllHere.call(remover)
    } catch (error) {
      out = unanswered(error)
    }
    if (out.kind === 'forgotten') {
      // The device's everything is its whole shelf, transcripts of explorations
      // no row names any more included. An account's is the list its remover
      // deleted and nothing past it: a shared graph it was only invited to is
      // still there, and so is the conversation about it.
      if (pressedBy.kind === 'device') forgetAllConversations(out.ids, pressedBy)
      else for (const id of out.ids) forgetConversations(id, pressedBy)
    }
    await answered(EVERYTHING, out, open !== null ? { id: open, blank } : null)
    return out
  })
}
